import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {
  AVAILABLE_PRIVILEGES,
  CombinedUserDto, deleteUserIdentity, deleteUserPortalAccount,
  fetchUser,
  PortalUserAccount,
  saveUserPortalAccount, setUserKeycloakRole, updateUserIdentity, UserIdentity
} from './usermanagement-lib';
import {
  Dimmer,
  Loader,
  Header,
  Icon,
  Form,
  Grid,
  Button,
  Radio,
  Input,
  Checkbox,
  List,
  Message, MessageHeader
} from 'semantic-ui-react';
import _get from 'lodash/get';
import _set from 'lodash/set';
import {CodeConfirmationModal} from '../../components/ConfirmModal/CodeConfirmationModal';
import {NavLink} from 'react-router-dom';
import {GUARD_ADMIN} from "../../routes";
import ErrorBox from "../../components/ErrorBox/ErrorBox";
import {AnalyticsTracking} from "../../components/AnalyticsTracking/AnalyticsTracking";

interface UserEditProps extends RouteComponentProps<{ userId: string; }> {
}

type UserEditMode = 'VIEW' | 'EDIT_IDENTITY' | 'EDIT_PROFILE' | 'DELETE_IDENTITY' | 'DELETE_PROFILE' | 'USER_DELETED';

interface UserEditState {
  loading: boolean;
  user?: CombinedUserDto;
  mode: UserEditMode;

  editProfile?: PortalUserAccount;
  editIdentity?: UserIdentity;

  error?: string;
}

class UserEdit extends React.Component<UserEditProps, UserEditState> {
  constructor(props: UserEditProps) {
    super(props);
    this.state = {
      loading: false,
      mode: 'VIEW'
    };
  }

  componentDidMount(): void {
    this.loadCurrentUser();
  }

  loadCurrentUser() {
    this.setState({
      loading: true,
      error: undefined,
      mode: 'VIEW'
    }, () => {
      fetchUser(this.props.match.params.userId).then(response => {
        this.setState({
          loading: false,
          user: response.data
        });
      }).catch(_ => {
        this.setState({
          loading: false,
          user: undefined
        });
      });
    })
  }

  canDeleteDisplayedIdentity() {
    return GUARD_ADMIN() && this.state.user && !this.state.user.portalAccount;
  }

  canModifyDisplayedUser() {
    if(GUARD_ADMIN()) {
      return true;
    }
    if(this.state.user && this.state.user.portalAccount && this.state.user.portalAccount.userRole === 'A') {
      return false;
    }
    if(this.state.user && this.state.user.portalAccount && this.state.user.portalAccount.privileges && this.state.user.portalAccount.privileges.indexOf('manage_users') !== -1) {
      return false;
    }
    return true;
  }

  canAssignAdministrativePermissions() {
    return GUARD_ADMIN();
  }

  renderPortalAccount() {
    if(!this.state.user) {
      return null;
    }
    if(!this.state.user.portalAccount) {
      return (<>
        <p>Dieser Benutzer ist noch nicht für das Informationssystem freigeschaltet.</p>
        <Button type="button" disabled={this.state.mode !== 'VIEW'} primary onClick={() => this.newProfile()}>Benutzer freischalten</Button>
        </>);
    }
    const portalAccount = this.state.user.portalAccount;
    const accounts = Array.isArray(portalAccount.accounts) ? portalAccount.accounts : [];
    const privileges = Array.isArray(portalAccount.privileges) ? portalAccount.privileges : [];


    return (<>
      <Form>
        <Form.Field>
          <label>Benutzerrolle</label>
          {portalAccount.userRole === 'A' && 'Administrator'}
          {portalAccount.userRole === 'P' && 'Premium'}
          {portalAccount.userRole === 'U' && 'Benutzer'}
        </Form.Field>

        <Form.Field>
          <label>Konten</label>
          <List>
            {accounts.map((acc, idx) => {
              return (
                  <List.Item key={'acc_' + idx}>
                    <strong>{acc.account}</strong>
                    {acc.roles.indexOf('Z') !== -1 && ' [Anlieferer]'}
                    {acc.roles.indexOf('K') !== -1 && ' [Kunde]'}
                  </List.Item>)
            })}
          </List>
        </Form.Field>

        <Form.Field>
          <label>Zugriffsrechte</label>
          <List>
          {privileges.map((priv, idx) => {
            let privName = priv;
            let foundPriv = AVAILABLE_PRIVILEGES.find(p => p.key === priv);
            if(foundPriv) {
              privName = foundPriv.label;
            }
            return <List.Item key={'priv_' + idx}>{privName}</List.Item>
          })}
          </List>
        </Form.Field>
        <Button type="button" disabled={this.state.mode !== 'VIEW' || !this.canModifyDisplayedUser()} basic color="red" onClick={() => this.setState({mode: 'DELETE_PROFILE'})}>Zugang löschen</Button>
        <Button type="button" disabled={this.state.mode !== 'VIEW' || !this.canModifyDisplayedUser()} primary onClick={() => this.editProfile()} >Bearbeiten</Button>
      </Form>
    </>);
  }

  newProfile() {
    this.setState({
      mode: 'EDIT_PROFILE',
      error: undefined,
      editProfile: {
        accounts: [],
        privileges: [],
        userRole: 'U',
        userType: 'K'
      }
    });
  }

  editProfile() {
    if(!this.state.user || !this.state.user.portalAccount) {
      return null;
    }

    this.setState({
      mode: 'EDIT_PROFILE',
      error: undefined,
      editProfile: JSON.parse(JSON.stringify(this.state.user.portalAccount))
    });
  }

  saveProfile() {
    if(!this.state.user ||!this.state.editProfile) {
      throw Error('Invalid state');
    }
    if(this.state.editProfile.accounts && Array.isArray(this.state.editProfile.accounts)) {
      for(let acc of this.state.editProfile.accounts) {
        if(typeof acc.account !== 'string' || acc.account.length !== 7 || !/^[0-9]+$/.test(acc.account)) {
          this.setState({error: 'Konten müssen 7-stellig sein und dürfen nur Nummern enthalten!'});
          return;
        }
      }
    }
    this.setState({
      loading: true,
      error: undefined
    }, () => {
      if(!this.state.user ||!this.state.editProfile) {
        throw Error('Invalid state');
      }
      saveUserPortalAccount(this.state.user.userId, this.state.editProfile)
        .then(() => {
          this.setState({
            loading: false
          }, () => this.loadCurrentUser());
        }).catch((err) => {
          let error = undefined as string | undefined;
          if(err.response && err.response.data && err.response.data.error) {
            if(err.response.data.error === 'invalid_account_no') {
              error = 'Konten müssen 7-stellig sein und dürfen nur Nummern enthalten!';
              ErrorBox.reset();
            }
          }
          this.setState({
            loading: false,
            error: error
          });
      });
    });
  }

  renderEditPortalAccount() {
    if(!this.state.editProfile ) {
      return null;
    }

    const portalAccount = this.state.editProfile;
    const accounts = Array.isArray(portalAccount.accounts) ? portalAccount.accounts : [];
    const privileges = Array.isArray(portalAccount.privileges) ? portalAccount.privileges : [];

    return (<>
      <Form onSubmit={() => this.saveProfile()}>
        <Form.Field>
          <label>Benutzertyp</label>
          <div><Radio checked={portalAccount.userType === 'K'} label={"Kunde"}
                      onChange={(_, data) => {
                        if(data.checked) {
                          this.setState((prevState) => {
                            return {
                              editProfile: {...prevState.editProfile, userType: 'K'}
                            }
                          })
                        }
                      }}
          /></div>
          <div>
            <Radio checked={portalAccount.userType === 'Z'} label={"Anlieferer"}
                   onChange={(_, data) => {
                     if(data.checked) {
                       this.setState((prevState) => {
                         return {
                           editProfile: {...prevState.editProfile, userType: 'Z'}
                         }
                       })
                     }
                   }}/>
          </div>
        </Form.Field>

        <Form.Field>
          <label>Benutzerrolle</label>
          <div>
            <Radio checked={portalAccount.userRole === 'U'} label={"Benutzer"}
                   onChange={(_, data) => {
                     if(data.checked) {
                       this.setState((prevState) => {
                         return {
                           editProfile: {...prevState.editProfile, userRole: 'U'}
                         }
                       })
                     }
                   }} />
          </div>
          <div>
            <Radio checked={portalAccount.userRole === 'P'} label={"Premium"}
                   disabled={!this.canAssignAdministrativePermissions()}
                   onChange={(_, data) => {
                     if(data.checked) {
                       this.setState((prevState) => {
                         return {
                           editProfile: {...prevState.editProfile, userRole: 'P'}
                         }
                       })
                     }
                   }}/>
          </div>
          <div>
          <Radio checked={portalAccount.userRole === 'A'} label={"Administrator"}
                 disabled={!this.canAssignAdministrativePermissions()}
                 onChange={(_, data) => {
                   if(data.checked) {
                     this.setState((prevState) => {
                       return {
                         editProfile: {...prevState.editProfile, userRole: 'A'}
                       }
                     })
                   }
                 }}/>
        </div>
        </Form.Field>

        <Form.Field>
          <label>Konten</label>
          <div style={{textAlign:'right'}}>
            <Button type="button" basic icon="plus" onClick={() => {
              this.setState((prevState) => {
                if(!prevState.editProfile) return null;
                const accounts = prevState.editProfile.accounts || [];
                accounts.push({account: '', roles: []});
                return {
                  editProfile: {...prevState.editProfile, accounts}
                }
              })
            }}/>
          </div>
          <List>

            {accounts.map((account, idx) => (
                <List.Item
                    key={idx + '_account'}>
                  <Input type="text" minLength="7" maxLength="7" required pattern="[0-9]+" title="7-stellig, nur Ziffern (0-9)"
                         value={account.account}
                         onChange={(_, data) => {
                           const newValue = data.value;
                           this.setState(prevState => {
                             if(!prevState.editProfile) return null;
                             const accounts = [...(prevState.editProfile.accounts||[])];
                             accounts[idx].account = newValue;
                             return {
                               editProfile: {...prevState.editProfile, accounts},
                               error: undefined
                             }
                           })
                         }}

                         icon={<Button type="button" icon="x" basic onClick={() => {
                           this.setState(prevState => {
                             if(!prevState.editProfile) return null;
                             const accounts = [...(prevState.editProfile.accounts||[])];
                             accounts.splice(idx, 1);
                             return {
                               editProfile: {...prevState.editProfile, accounts},
                               error: undefined
                             }
                           })
                         }}/>}
                  />
                  <div style={{padding: '1em'}}>
                  <Checkbox label="Kunde"
                            checked={account.roles.indexOf('K') !== -1}
                            onClick={(_, data) => {
                              this.setState(prevState => {
                                if(!prevState.editProfile) return null;
                                const accounts = [...(prevState.editProfile.accounts||[])];
                                if(data.checked) {
                                  if(accounts[idx].roles.indexOf('K') === -1) {
                                    accounts[idx].roles.push('K');
                                  }
                                } else {
                                  accounts[idx].roles = accounts[idx].roles.filter(r => r !== 'K');
                                }
                                return {
                                  editProfile: {...prevState.editProfile, accounts}
                                }
                              })
                            }}
                  /><Checkbox label="Anlieferer"
                              style={{marginLeft: '1em'}}
                              checked={account.roles.indexOf('Z') !== -1}
                              onClick={(_, data) => {
                                this.setState(prevState => {
                                  if(!prevState.editProfile) return null;
                                  const accounts = [...(prevState.editProfile.accounts||[])];
                                  if(data.checked) {
                                    if(accounts[idx].roles.indexOf('Z') === -1) {
                                      accounts[idx].roles.push('Z');
                                    }
                                  } else {
                                    accounts[idx].roles = accounts[idx].roles.filter(r => r !== 'Z');
                                  }
                                  return {
                                    editProfile: {...prevState.editProfile, accounts}
                                  }
                                })
                              }}
                />
                  </div>
                </List.Item>
            ))}
          </List>
        </Form.Field>

        <Form.Field>
          <label>Zugriffsrechte</label>
          {AVAILABLE_PRIVILEGES.map(privilege => {
            return (<div key={'privilege_' + privilege.key}>
                <Checkbox
                  checked={privileges.indexOf(privilege.key) !== -1}
                  label={privilege.label}
                  disabled={privilege.administrative && !this.canAssignAdministrativePermissions()}
                  onChange={(_, data) => {
                    if(data.checked) {
                      this.setState((prevState) => {
                        if(!prevState.editProfile) return null;
                        const privileges = prevState.editProfile.privileges || [];
                        privileges.push(privilege.key);
                        return {
                          editProfile: {...prevState.editProfile, privileges}
                        }
                      });
                    } else {
                      this.setState((prevState) => {
                        if(!prevState.editProfile) return null;
                        const privileges = prevState.editProfile.privileges || [];
                        const pIdx = privileges.indexOf(privilege.key);
                        privileges.splice(pIdx, 1);
                        return {
                          editProfile: {...prevState.editProfile, privileges}
                        }
                      });
                    }
                  }}
                />
              </div>
            )
          })}
        </Form.Field>

        <Button type="button" onClick={() => this.setState({
          mode: 'VIEW',
          editProfile: undefined
        })}>Abbrechen</Button>

        <Button primary type="submit">Speichern</Button>
      </Form>
      {this.state.error && <Message warning content={this.state.error} />}
    </>);
  }

  setRole(role: string, enable: boolean|undefined) {

    if (this.state.user) {
      setUserKeycloakRole(this.state.user?.userId, role, !!enable)
          .then(
              (response) => {
                this.loadCurrentUser();
              }
          )
    }

  }

  renderApplicationRoles() {
    const user = this.state.user;
    if(!user || !user.identity) {
      return null;
    }
    return (
        <Form>
          <Form.Checkbox type="checkbox" checked={user.roles?.includes("datev")} onChange={(event,v) => this.setRole("datev", v.checked)} label="Datev">
          </Form.Checkbox>
          <Form.Checkbox type="checkbox" checked={user.roles?.includes("feldverladung")} onChange={(event,v) => this.setRole("feldverladung", v.checked)} label="Feldverladung">
          </Form.Checkbox>
        </Form>
    )
  }

  /*
     <Form.Checkbox type="checkbox" checked={user.identity.roles?.includes("webservices")} onChange={(event,v) => this.setRole("webservices", v.checked)} label="Webservices">
      </Form.Checkbox>

   */

  renderViewIdentity() {
    const user = this.state.user;
    if(!user || !user.identity) {
      return null;
    }
    return (
        <Form>
          <Form.Field>
            <label>E-Mail</label>
            {_get(user, 'identity.email') || '-'}
          </Form.Field>

          <Form.Field>
            <label>Firma</label>
            {_get(user, 'identity.attributes.company.0') || '-'}
          </Form.Field>

          <Form.Field>
            <label>Vorname</label>
            {_get(user, 'identity.firstName') || '-'}
          </Form.Field>

          <Form.Field>
            <label>Nachname</label>
            {_get(user, 'identity.lastName') || '-'}
          </Form.Field>

          <Form.Field>
            <label>E-Mail-Adresse bestätigt</label>
            {_get(user, 'identity.emailVerified')
                ? <span style={{color: 'green'}}>Ja</span>
                : <span style={{color: 'red'}}>Nein</span>}
          </Form.Field>

          {GUARD_ADMIN() && <Button type="button" disabled={this.state.mode !== 'VIEW' || !this.canDeleteDisplayedIdentity()} basic color="red" onClick={() => this.setState({mode: 'DELETE_IDENTITY'})}>Benutzer löschen</Button>}
          <Button type="button" disabled={this.state.mode !== 'VIEW' || !this.canModifyDisplayedUser()} primary onClick={() => this.editIdentity()}>Bearbeiten</Button>
          {this.state.mode === 'USER_DELETED' && (<>
            <br /><br />
            <Message positive>
              <MessageHeader>Benutzer gelöscht</MessageHeader>
            </Message>
            </>)}
        </Form>
    )
  }

  editIdentity() {
    if(!this.state.user) return;
    this.setState({
      mode: 'EDIT_IDENTITY',
      error: undefined,
      editIdentity: JSON.parse(JSON.stringify(this.state.user.identity))
    });
  }

  saveIdentity() {
    this.setState({
      loading: true,
      error: undefined
    }, () => {
      if(!this.state.user || !this.state.editIdentity) {
        throw Error('Invalid State');
      }
      updateUserIdentity(this.state.user.userId, this.state.editIdentity)
      .then(() => {
        this.setState({
          loading: false
        }, () => this.loadCurrentUser());
      }).catch(() => {
        this.setState({
          loading: false
        });
      });
    })
  }

  deleteIdentity() {
    this.setState({
      loading: true,
      error: undefined
    }, () => {
      if(!this.state.user) {
        throw Error('Invalid State');
      }
      deleteUserIdentity(this.state.user.userId)
        .then(() => {
          this.setState({
            loading: false
          }, () =>  this.setState({mode: 'USER_DELETED'}));
        }).catch(() => {
        this.setState({
          loading: false
        });
      });
    });
  }

  deleteProfile() {
    this.setState({
      loading: true,
      error: undefined
    }, () => {
      if(!this.state.user) {
        throw Error('Invalid State');
      }
      deleteUserPortalAccount(this.state.user.userId)
        .then(() => {
          this.setState({
            loading: false
          }, () => this.loadCurrentUser());
        }).catch(() => {
        this.setState({
          loading: false
        });
      });
    });
  }

  renderEditIdentity() {
    if(!this.state.editIdentity) {
      return null;
    }
    const identity = this.state.editIdentity;
    return (
        <Form>
          <Form.Field>
            <label>E-Mail</label>
            <Input disabled readonly value={identity.email} />
          </Form.Field>

          <Form.Field>
            <label>Firma</label>
            <Input value={_get(identity, 'attributes.company.0', '')}
              onChange={(_, data) => {
                this.setState(prevState => {
                  const newIdentity = {...prevState.editIdentity as UserIdentity};
                  _set(newIdentity, 'attributes.company.0', data.value);
                  return {
                    editIdentity: newIdentity
                  }
                })
              }}
            />
          </Form.Field>

          <Form.Field>
            <label>Vorname</label>
            <Input value={identity.firstName}
                   onChange={(_, data) => {
                     this.setState(prevState => {
                       return {
                         editIdentity: {...prevState.editIdentity as UserIdentity, firstName: data.value}
                       };
                     })
                   }}/>
          </Form.Field>

          <Form.Field>
            <label>Nachname</label>
            <Input value={identity.lastName}
                   onChange={(_, data) => {
                     this.setState(prevState => {
                       return {
                         editIdentity: {...prevState.editIdentity as UserIdentity, lastName: data.value}
                       };
                     })
                   }}/>
          </Form.Field>

          <Button type="button" onClick={() => this.setState({
            mode: 'VIEW',
            editIdentity: undefined
          })}>Abbrechen</Button>
          <Button type="button" primary onClick={() => this.saveIdentity()}>Speichern</Button>
        </Form>
    );
  }


  public render() {
    const user = this.state.user;
    return (
        <>
          <AnalyticsTracking page="user_management/edit" />
          <div className={"ui container"}>
            <Header as='h1'>
              <Icon name="users" />
              <Header.Content>
                Benutzerverwaltung
                <Header.Subheader>
                  {_get(user, 'identity.username')}
                </Header.Subheader>
              </Header.Content>
            </Header>

            <p>
              <NavLink to={`/settings/users`}>
                <Icon name="arrow left" /> Zurück
              </NavLink>
            </p>

            <Grid columns="3" stackable>
              <Grid.Column>
                <Header as='h2'>Identität</Header>
                {this.state.mode === 'EDIT_IDENTITY'
                    ? this.renderEditIdentity()
                    : this.renderViewIdentity()}
              </Grid.Column>
              <Grid.Column>
                <Header as='h2'>Informationssystem</Header>
                {this.state.mode === 'EDIT_PROFILE'
                   ? this.renderEditPortalAccount()
                   : this.renderPortalAccount()}
              </Grid.Column>
              <Grid.Column>
                <Header as='h2'>Applikationsrollen</Header>
                {this.state.mode === 'EDIT_PROFILE'
                  ? this.renderApplicationRoles()
                  : this.renderApplicationRoles()}
              </Grid.Column>
            </Grid>

            {this.state.mode === 'DELETE_PROFILE' && (
              <CodeConfirmationModal
                visible={true}
                title={'Portalzugang löschen'}
                onConfirm={() => this.deleteProfile()}
                onCancel={() => this.setState({mode: 'VIEW'})}
                loading={this.state.loading}
                confirmButtonProps={{color: 'red'}}
              >
                <p>Möchten Sie dem Benutzer {this.state.user && this.state.user.userId} den Zugang zum Informationssystem entziehen?</p>
              </CodeConfirmationModal>
            )}
            {this.state.mode === 'DELETE_IDENTITY' && (
              <CodeConfirmationModal
                visible={true}
                title={'Benutzer löschen'}
                onConfirm={() => this.deleteIdentity()}
                onCancel={() => this.setState({mode: 'VIEW'})}
                loading={this.state.loading}
                confirmButtonProps={{color: 'red'}}
              >
                <p>Möchten Sie den Benutzer {this.state.user && this.state.user.userId} vollständig löschen?</p>
              </CodeConfirmationModal>
            )}


            <Dimmer active={this.state.loading} inverted>
              <Loader/>
            </Dimmer>

          </div>
        </>
    );
  }

}

export default withRouter(UserEdit);